// components/FileUpload.tsx
import React, { useState, useRef } from 'react';
import { api } from '../config/api.ts';
import { ModernButton } from './ui/ModernButton.tsx';
import { ModernCard } from './ui/ModernCard.tsx';

interface UploadedFile {
  id: number;
  name: string;
  size?: number;
  type?: string;
  uploaded_at: string;
}

interface FileUploadProps {
  sessionId: string;
  onFilesChange: (files: UploadedFile[]) => void;
  maxFiles?: number;
  maxSize?: number; // in MB
  acceptedTypes?: string;
  endpoint?: string; // '/api/client/tasks/upload-files' or '/api/client/bookings/upload-files'
  deleteEndpoint?: string; // '/api/client/tasks/delete-files' or '/api/client/bookings/files'
  disabled?: boolean;
  mode?: 'task' | 'booking'; // For different validation rules
}

export const FileUpload: React.FC<FileUploadProps> = ({
  sessionId,
  onFilesChange,
  maxFiles = 10,
  maxSize = 20, // 20MB default
  acceptedTypes = ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.zip",
  endpoint = '/client/tasks/upload-files', // Default to tasks
  deleteEndpoint,
  disabled = false,
  mode = 'task'
}) => {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Auto-determine delete endpoint if not provided
  const actualDeleteEndpoint = deleteEndpoint || (
    mode === 'booking' ? '/client/bookings/files' : '/client/tasks/delete-files'
  );

  // Get file size limit based on mode
  const getMaxSizeForMode = () => {
    if (mode === 'booking') return 500; // 500MB for booking files (like original)
    return maxSize; // Custom or default for tasks
  };

  const handleFileUpload = async (files: FileList) => {
    if (files.length === 0) return;
    if (uploadedFiles.length + files.length > maxFiles) {
      alert(`يمكنك رفع حتى ${maxFiles} ملفات فقط`);
      return;
    }

    // Validate file sizes
    const maxSizeBytes = getMaxSizeForMode() * 1024 * 1024;
    const oversizedFiles = Array.from(files).filter(file => file.size > maxSizeBytes);
    if (oversizedFiles.length > 0) {
      alert(`بعض الملفات تتجاوز الحد الأقصى للحجم (${getMaxSizeForMode()} ميجابايت)`);
      return;
    }

    // Validate file types
    const allowedExtensions = acceptedTypes.split(',').map(ext => ext.trim().toLowerCase());
    const invalidFiles = Array.from(files).filter(file => {
      const extension = '.' + file.name.split('.').pop()?.toLowerCase();
      return !allowedExtensions.includes(extension);
    });
    if (invalidFiles.length > 0) {
      alert('بعض الملفات غير مدعومة. الملفات المدعومة: ' + acceptedTypes);
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      Array.from(files).forEach(file => {
        formData.append('files[]', file);
      });
      formData.append('session_id', sessionId);

      const response = await api.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        const newFiles = [...uploadedFiles, ...response.data.data.files];
        setUploadedFiles(newFiles);
        onFilesChange(newFiles);
      } else {
        throw new Error(response.data.message || 'فشل في رفع الملفات');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'خطأ غير معروف';
      alert('فشل في رفع الملفات: ' + errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleFileDelete = async (fileId: number) => {
    try {
      const deleteData = {
        session_id: sessionId,
        file_ids: [fileId],
      };

      const response = await api.delete(actualDeleteEndpoint, {
        data: deleteData
      });

      if (response.data.success) {
        const updatedFiles = uploadedFiles.filter(file => file.id !== fileId);
        setUploadedFiles(updatedFiles);
        onFilesChange(updatedFiles);
      } else {
        throw new Error(response.data.message || 'فشل في حذف الملف');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'خطأ غير معروف';
      alert('فشل في حذف الملف: ' + errorMessage);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFileUpload(e.target.files);
      e.target.value = ''; // Reset input
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (filename: string) => {
    const extension = filename.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf': return '📄';
      case 'doc':
      case 'docx': return '📝';
      case 'xls':
      case 'xlsx': return '📊';
      case 'ppt':
      case 'pptx': return '📊';
      case 'txt': return '📄';
      case 'jpg':
      case 'jpeg':
      case 'png': return '🖼️';
      case 'zip': return '📦';
      default: return '📎';
    }
  };

  const getHelpText = () => {
    if (mode === 'booking') {
      return {
        title: 'ملفات المادة',
        description: 'يرجى تحميل ملفات الجباتر أو السلايدات أو صورة من إعلان الجامعة',
        supportedFiles: 'PDF, DOC, JPG, PNG',
        maxSize: `${getMaxSizeForMode()} ميجابايت`
      };
    } else {
      return {
        title: 'ملفات المهمة',
        description: 'ارفع الملفات المتعلقة بمهمتك',
        supportedFiles: 'PDF, DOC, XLS, PPT, TXT, JPG, PNG, ZIP',
        maxSize: `${getMaxSizeForMode()} ميجابايت`
      };
    }
  };

  const helpText = getHelpText();

  return (
    <div className="space-y-4">
      {/* Upload Area */}
      <ModernCard className="p-6">
        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            dragActive
              ? 'border-blue-500 bg-blue-50'
              : disabled
              ? 'border-gray-200 bg-gray-50'
              : 'border-gray-300 hover:border-blue-400'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={acceptedTypes}
            onChange={handleFileSelect}
            className="hidden"
            disabled={disabled || uploading}
          />
          
          <div className="space-y-4">
            <div className="text-4xl text-gray-400">
              📎
            </div>
            
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                {helpText.title}
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                اسحب الملفات هنا أو انقر للاختيار
              </p>
              {mode === 'booking' && (
                <p className="text-sm text-yellow-700 bg-yellow-50 border border-yellow-200 rounded p-2 mt-2">
                  {helpText.description}
                </p>
              )}
            </div>
            
            <ModernButton
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled || uploading}
              loading={uploading}
            >
              {uploading ? 'جاري الرفع...' : 'اختر الملفات'}
            </ModernButton>
            
            <div className="text-xs text-gray-500">
              <p>الملفات المدعومة: {helpText.supportedFiles}</p>
              <p>الحد الأقصى: {maxFiles} ملفات، {helpText.maxSize} لكل ملف</p>
            </div>
          </div>
        </div>
      </ModernCard>

      {/* Uploaded Files List */}
      {uploadedFiles.length > 0 && (
        <ModernCard className="p-4">
          <h4 className="font-medium text-gray-900 mb-3">
            الملفات المرفوعة ({uploadedFiles.length})
          </h4>
          
          <div className="space-y-2">
            {uploadedFiles.map((file) => (
              <div
                key={file.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center space-x-3 space-x-reverse">
                  <div className="text-blue-600 text-xl">
                    {getFileIcon(file.name)}
                  </div>
                  <div>
                    <div className="font-medium text-sm text-gray-900">
                      {file.name}
                    </div>
                    <div className="text-xs text-gray-500">
                      {file.size && formatFileSize(file.size)}
                      {file.size && ' • '}
                      تم الرفع في {new Date(file.uploaded_at).toLocaleString('ar')}
                    </div>
                  </div>
                </div>
                
                <button
                  onClick={() => handleFileDelete(file.id)}
                  className="text-red-600 hover:text-red-700 p-1 hover:bg-red-50 rounded transition-colors"
                  disabled={disabled}
                  title="حذف الملف"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </ModernCard>
      )}
    </div>
  );
};